import { forwardRef, type CSSProperties } from "react";
import FbkCardShell from "./FbkCardShell";
import { getNationColors } from "./nationColors";
import {
  defaultFooterLeft,
  resolveTextStyle,
  type CardFormat,
  type MatchdayCardContent,
  type MatchdayNation,
} from "./types";

/**
 * Rough luminance check for #RRGGBB. Returns 0 (dark) to 1 (light).
 * Used by monochrome theme to pick legible text colors on the dominant fill.
 */
function luminance(hex: string): number {
  const c = hex.replace("#", "");
  if (c.length !== 6) return 0;
  const r = parseInt(c.slice(0, 2), 16) / 255;
  const g = parseInt(c.slice(2, 4), 16) / 255;
  const b = parseInt(c.slice(4, 6), 16) / 255;
  // ITU-R BT.709 weights
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

interface MatchdayCardProps {
  format: CardFormat;
  content: MatchdayCardContent;
}

const FONT_BEBAS = "'Bebas Neue', 'Anton', sans-serif";
const FONT_INTER = "'Inter', system-ui, sans-serif";
const FONT_PLAYFAIR = "'Playfair Display', Georgia, serif";

// Editorial cream + dark text palette
const EDITORIAL_BG = "#F4EDD9";
const EDITORIAL_INK = "#1A2E1F";
const EDITORIAL_ACCENT = "#A02928"; // muted oxblood red — newsroom feel

/** flagcdn fallback when the nation doesn't have a flagUrl from the DB. */
function flagSrc(nation: MatchdayNation): string | undefined {
  if (nation.flagUrl) return nation.flagUrl;
  if (nation.code) return `https://flagcdn.com/w320/${nation.code.toLowerCase()}.png`;
  return undefined;
}

const MatchdayCard = forwardRef<HTMLDivElement, MatchdayCardProps>(
  function MatchdayCard({ format, content }, ref) {
    const isStory = format === "story";
    const style = content.style ?? {};
    const theme = content.theme;

    // The fight-poster themes (rivalry / duel / marquee) need a fundamentally
    // different composition from the shared layout below — giant flag halves,
    // dramatic centre headlines, large player portraits. Branch out early so
    // the existing 9 themes stay untouched.
    if (theme === "rivalry" || theme === "duel" || theme === "marquee") {
      return (
        <FightPosterLayout
          ref={ref}
          format={format}
          content={content}
          theme={theme}
        />
      );
    }

    // Resolve colors for split / monochrome themes
    const homeColors = getNationColors(content.home.code);
    const awayColors = getNationColors(content.away.code);
    const homeColor = content.homeColor || homeColors.primary;
    const awayColor = content.awayColor || awayColors.primary;

    // For monochrome — figure out which side dominates + pick legible text
    const monoSide = content.monoSide ?? "home";
    const monoBg = monoSide === "home" ? homeColor : awayColor;
    const monoBgIsLight = luminance(monoBg) > 0.55;
    const monoTextPrimary = monoBgIsLight ? "#07321A" : "#FFFFFF";
    const monoTextAccent = monoBgIsLight ? "#0D5C2E" : "#FFD700";

    // Custom background per theme
    let customBaseGradient: string | undefined;
    if (theme === "split") {
      // Hairline gold seam (~0.3% = ~3px at 1080w) — reads as a clean divider, not a band
      customBaseGradient = `linear-gradient(90deg, ${homeColor} 0%, ${homeColor} 49.85%, #FFD700 49.85%, #FFD700 50.15%, ${awayColor} 50.15%, ${awayColor} 100%)`;
    } else if (theme === "spotlight") {
      // Pitch-black with a strong radial halo behind the centerpiece — stadium-night vibe
      customBaseGradient =
        "radial-gradient(ellipse 70% 60% at 50% 48%, #1c3a25 0%, #0b1f12 50%, #000000 100%)";
    } else if (theme === "pitch") {
      // Flat pitch-grass green (lighter and more uniform than the brand gradient)
      customBaseGradient =
        "linear-gradient(180deg, #186b32 0%, #135828 50%, #0e4a21 100%)";
    } else if (theme === "monochrome") {
      customBaseGradient = monoBg;
    } else if (theme === "aurora") {
      // Smooth atmospheric blend of home → gold → away. Multi-radial for soft edges.
      customBaseGradient = [
        `radial-gradient(ellipse at 15% 20%, ${homeColor} 0%, transparent 55%)`,
        `radial-gradient(ellipse at 85% 80%, ${awayColor} 0%, transparent 55%)`,
        `radial-gradient(ellipse at 50% 50%, rgba(255,215,0,0.35) 0%, transparent 60%)`,
        "linear-gradient(135deg, #0D5C2E 0%, #07321A 100%)",
      ].join(", ");
    } else if (theme === "editorial") {
      // Warm cream paper — overlays will be suppressed for clean magazine feel
      customBaseGradient = EDITORIAL_BG;
    } else if (theme === "halftone") {
      // Two-tone base; the dot pattern overlay handles the texture
      customBaseGradient = `linear-gradient(180deg, ${homeColor} 0%, ${homeColor} 50%, #1A1A1A 50%, #1A1A1A 100%)`;
    } else if (theme === "stripes") {
      // Bold diagonal stripes: home / gold seam / away / gold seam / home
      customBaseGradient = `linear-gradient(135deg,
        ${homeColor} 0%, ${homeColor} 28%,
        #FFD700 28%, #FFD700 30%,
        ${awayColor} 30%, ${awayColor} 70%,
        #FFD700 70%, #FFD700 72%,
        ${homeColor} 72%, ${homeColor} 100%)`;
    }

    // Editorial uses serif + dark ink; other themes keep the display sans
    const isEditorial = theme === "editorial";
    const fontHeadline = isEditorial ? FONT_PLAYFAIR : FONT_BEBAS;
    const fontEyebrow = isEditorial ? FONT_INTER : FONT_INTER;

    // Pick the headline / accent colors per theme
    const isDarkTheme =
      theme === "signature" ||
      theme === "split" ||
      theme === "spotlight" ||
      theme === "pitch" ||
      theme === "aurora" ||
      theme === "stripes";
    const headlineColor = isEditorial
      ? EDITORIAL_INK
      : theme === "monochrome"
        ? monoTextPrimary
        : theme === "halftone"
          ? "#FFFFFF"
          : "#FFFFFF";
    const accentColor = isEditorial
      ? EDITORIAL_ACCENT
      : theme === "monochrome"
        ? monoTextAccent
        : "#FFD700";
    const subtleTextColor = isEditorial
      ? "rgba(26,46,31,0.75)"
      : theme === "monochrome"
        ? (monoBgIsLight ? "rgba(0,0,0,0.7)" : "rgba(255,255,255,0.85)")
        : "rgba(255,255,255,0.95)";

    // ─── Type styles (theme-aware) ──────────────────────────
    // For monochrome on a dark color, "gold" highlight stays gold;
    // on a light color we swap to a dark accent for legibility.
    const eyebrowStyle: CSSProperties = {
      ...resolveTextStyle(
        style.eyebrow,
        {
          fontFamily: fontEyebrow,
          fontSizePx: 22,
          color: accentColor,
          letterSpacing: "0.5em",
          bold: true,
          align: "center",
        },
        format,
      ),
      textTransform: "uppercase",
      marginTop: isStory ? 56 : 32,
    };

    // In monochrome, the dominant name is much bigger than the other
    const isMono = theme === "monochrome";
    const headlineDefaultSize = isStory ? 168 : 132;
    const dominantSize = isMono ? (isStory ? 220 : 168) : headlineDefaultSize;

    // Editorial uses italic serif at slightly smaller size for elegance
    const homeNameStyle: CSSProperties = {
      ...resolveTextStyle(
        style.headline,
        {
          fontFamily: fontHeadline,
          fontSizePx: isMono && monoSide === "home"
            ? dominantSize
            : isEditorial
              ? Math.round(headlineDefaultSize * 0.78)
              : headlineDefaultSize,
          color: isMono && monoSide === "home" ? headlineColor : headlineColor,
          letterSpacing: isEditorial ? "-0.01em" : "0.005em",
          bold: isEditorial ? true : false,
          italic: isEditorial,
          align: "center",
        },
        format,
      ),
      lineHeight: isEditorial ? 1.0 : 0.9,
    };
    const awayNameStyle: CSSProperties = {
      ...resolveTextStyle(
        style.goldHighlight,
        {
          fontFamily: fontHeadline,
          fontSizePx: isMono && monoSide === "away"
            ? dominantSize
            : isEditorial
              ? Math.round(headlineDefaultSize * 0.78)
              : headlineDefaultSize,
          color: accentColor,
          letterSpacing: isEditorial ? "-0.01em" : "0.005em",
          bold: isEditorial ? true : false,
          italic: isEditorial,
          align: "center",
        },
        format,
      ),
      lineHeight: isEditorial ? 1.0 : 0.9,
      fontSize:
        (style.headline?.fontSizePx ??
          style.goldHighlight?.fontSizePx ??
          (isMono && monoSide === "away"
            ? dominantSize
            : isEditorial
              ? Math.round(headlineDefaultSize * 0.78)
              : headlineDefaultSize)) *
        (isStory ? 1 : 0.78),
    };

    const vsStyle: CSSProperties = {
      fontFamily: FONT_BEBAS,
      fontSize: isStory ? 64 : 48,
      color: theme === "monochrome"
        ? (monoBgIsLight ? "rgba(0,0,0,0.5)" : "rgba(255,215,0,0.85)")
        : "rgba(255,215,0,0.85)",
      lineHeight: 1,
      letterSpacing: "0.05em",
      margin: isStory ? "8px 0" : "6px 0",
      textAlign: "center",
    };

    const taglineBarColor = theme === "monochrome" ? monoTextAccent : "#FFD700";
    const taglineStyle: CSSProperties = {
      ...resolveTextStyle(
        style.tagline,
        {
          fontFamily: FONT_INTER,
          fontSizePx: 28,
          color: theme === "monochrome"
            ? subtleTextColor
            : "rgba(255,215,0,0.95)",
          bold: false,
          italic: true,
          align: "center",
        },
        format,
      ),
      lineHeight: 1.3,
      marginTop: isStory ? 28 : 18,
      paddingLeft: isStory ? 28 : 22,
      paddingRight: isStory ? 28 : 22,
      borderLeft: `4px solid ${taglineBarColor}`,
      borderRight: `4px solid ${taglineBarColor}`,
      alignSelf: "center",
      maxWidth: "85%",
    };

    const statLineStyle: CSSProperties = {
      ...resolveTextStyle(
        style.body,
        {
          fontFamily: FONT_INTER,
          fontSizePx: 24,
          color: subtleTextColor,
          bold: true,
          align: "center",
        },
        format,
      ),
      letterSpacing: "0.18em",
      textTransform: "uppercase",
      lineHeight: 1.5,
    };
    // Reference to avoid TS unused — kept for theme awareness in future renders
    void isDarkTheme;

    const ctaFontSize = isStory ? 26 : 22;
    const ctaStyle: CSSProperties = {
      ...resolveTextStyle(
        style.ctaText,
        { fontFamily: FONT_INTER, fontSizePx: 26, color: "#0A4724", bold: true },
        format,
      ),
      display: "inline-flex",
      alignItems: "center",
      gap: 10,
      marginTop: isStory ? 36 : 22,
      padding: isStory ? "20px 36px" : "16px 28px",
      borderRadius: 9999,
      background: "#FFD700",
      fontSize: ctaFontSize,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      boxShadow: "0 20px 50px rgba(255, 215, 0, 0.3)",
      alignSelf: "center",
      textAlign: undefined,
    };

    // ─── Sizing ─────────────────────────────────────────────
    const flagWidth = isStory ? 220 : 170;
    const flagHeight = isStory ? 150 : 116;
    const playerSize = isStory ? 130 : 100;

    // ─── Featured player tile ───────────────────────────────
    const renderPlayer = (
      player: MatchdayCardContent["featuredHome"] | MatchdayCardContent["featuredAway"],
      side: "home" | "away",
    ) => {
      if (!player) return <div style={{ width: playerSize }} />;
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 8,
            width: playerSize + 24,
          }}
        >
          <div
            style={{
              width: playerSize,
              height: playerSize,
              borderRadius: 9999,
              overflow: "hidden",
              border: `3px solid ${side === "home" ? "rgba(255,255,255,0.7)" : "#FFD700"}`,
              background: "rgba(255,255,255,0.06)",
              boxShadow: "0 12px 30px rgba(0,0,0,0.45)",
            }}
          >
            {player.photoUrl ? (
              <img
                src={player.photoUrl}
                alt={player.name}
                crossOrigin={player.photoUrl.startsWith("data:") ? undefined : "anonymous"}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontFamily: FONT_BEBAS,
                  fontSize: playerSize * 0.5,
                  color: "rgba(255,255,255,0.4)",
                }}
              >
                {player.name?.charAt(0) || "?"}
              </div>
            )}
          </div>
          <div
            style={{
              fontFamily: FONT_INTER,
              fontSize: isStory ? 18 : 16,
              fontWeight: 800,
              color: "#FFFFFF",
              letterSpacing: "0.04em",
              textAlign: "center",
              maxWidth: playerSize + 24,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {player.name}
          </div>
        </div>
      );
    };

    const homeFlag = flagSrc(content.home);
    const awayFlag = flagSrc(content.away);

    return (
      <FbkCardShell
        ref={ref}
        format={format}
        footerLeft={content.footerLeft || defaultFooterLeft("MATCHDAY")}
        footerRight={content.hashtags || "#WC2026 · #MATCHDAY · #FBK"}
        header={content.header}
        background={style.background}
        customBaseGradient={customBaseGradient}
        headerOnLight={isEditorial || (isMono && monoBgIsLight)}
        suppressBrandOverlays={
          isEditorial || theme === "halftone" || theme === "stripes" || theme === "pitch"
        }
        hideFooter={content.footer?.hide}
      >
        {/* Theme-specific decorative overlays — sit below content, above shell */}
        {theme === "spotlight" && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
              background:
                "radial-gradient(circle at 50% 48%, rgba(255, 215, 0, 0.32) 0%, rgba(255, 215, 0, 0.12) 25%, transparent 55%)",
              zIndex: 1,
            }}
          />
        )}
        {theme === "halftone" && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
              zIndex: 1,
              opacity: 0.5,
              backgroundImage:
                "radial-gradient(circle, #000 1.5px, transparent 2px)",
              backgroundSize: "14px 14px",
              mixBlendMode: "multiply",
            }}
          />
        )}
        {theme === "editorial" && (
          // Thin gold rule top + bottom for that magazine-spread feel
          <>
            <div
              style={{
                position: "absolute",
                top: isStory ? 48 : 32,
                left: isStory ? 80 : 56,
                right: isStory ? 80 : 56,
                height: 1,
                background: "rgba(160, 41, 40, 0.4)",
                pointerEvents: "none",
                zIndex: 1,
              }}
            />
            <div
              style={{
                position: "absolute",
                bottom: isStory ? 48 : 32,
                left: isStory ? 80 : 56,
                right: isStory ? 80 : 56,
                height: 1,
                background: "rgba(160, 41, 40, 0.4)",
                pointerEvents: "none",
                zIndex: 1,
              }}
            />
          </>
        )}
        {theme === "pitch" && (
          <svg
            viewBox="0 0 1080 1920"
            preserveAspectRatio="none"
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              pointerEvents: "none",
              zIndex: 1,
              opacity: 0.55,
            }}
          >
            <g
              fill="none"
              stroke="rgba(255,255,255,0.85)"
              strokeWidth="3"
              strokeLinecap="square"
            >
              {/* Outer touchline */}
              <rect x="60" y="60" width="960" height="1800" />
              {/* Halfway line */}
              <line x1="60" y1="960" x2="1020" y2="960" />
              {/* Center circle */}
              <circle cx="540" cy="960" r="170" />
              {/* Center dot */}
              <circle cx="540" cy="960" r="6" fill="rgba(255,255,255,0.85)" />
              {/* Top penalty box */}
              <rect x="240" y="60" width="600" height="260" />
              {/* Top goal area */}
              <rect x="380" y="60" width="320" height="110" />
              {/* Top penalty arc */}
              <path d="M 460 320 A 80 80 0 0 0 620 320" />
              {/* Bottom penalty box */}
              <rect x="240" y="1600" width="600" height="260" />
              {/* Bottom goal area */}
              <rect x="380" y="1750" width="320" height="110" />
              {/* Bottom penalty arc */}
              <path d="M 460 1600 A 80 80 0 0 1 620 1600" />
              {/* Corner arcs */}
              <path d="M 60 84 A 24 24 0 0 0 84 60" />
              <path d="M 1020 84 A 24 24 0 0 1 996 60" />
              <path d="M 60 1836 A 24 24 0 0 1 84 1860" />
              <path d="M 1020 1836 A 24 24 0 0 0 996 1860" />
            </g>
          </svg>
        )}

        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            gap: 0,
            minHeight: 0,
            position: "relative",
            zIndex: 2,
          }}
        >
          {/* Eyebrow */}
          <div style={eyebrowStyle}>{content.eyebrow}</div>

          {/* Names block */}
          <div
            style={{
              marginTop: isStory ? 36 : 26,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 0,
            }}
          >
            <div style={homeNameStyle}>{content.home.name}</div>
            <div style={vsStyle}>vs</div>
            <div style={awayNameStyle}>{content.away.name}.</div>
          </div>

          {/* Flags + VS seam */}
          <div
            style={{
              marginTop: isStory ? 40 : 28,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: isStory ? 36 : 26,
            }}
          >
            {homeFlag && (
              <div
                style={{
                  width: flagWidth,
                  height: flagHeight,
                  borderRadius: 12,
                  // No border, no shadow — crest sits flush on the card background.
                  background: "transparent",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                }}
              >
                <img
                  src={homeFlag}
                  alt={content.home.name}
                  // No crossOrigin — many club CDNs (premierleague.com) don't
                  // return CORS headers and setting it blocks display entirely.
                  // For reliable PNG export, upload the crest via the form's
                  // file picker (data URL bypasses CORS).
                  style={{
                    width: "100%",
                    height: "100%",
                    // contain for both flags and crests — preserves aspect,
                    // never crops, identical treatment regardless of image type.
                    objectFit: "contain",
                  }}
                />
              </div>
            )}
            <div
              style={{
                fontFamily: FONT_BEBAS,
                fontSize: isStory ? 96 : 72,
                color: isMono && monoBgIsLight ? "#07321A" : "#FFD700",
                lineHeight: 1,
                letterSpacing: "0.06em",
                textShadow:
                  isMono && monoBgIsLight
                    ? "none"
                    : "0 6px 24px rgba(255,215,0,0.45)",
              }}
            >
              VS
            </div>
            {awayFlag && (
              <div
                style={{
                  width: flagWidth,
                  height: flagHeight,
                  borderRadius: 12,
                  background: "transparent",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                }}
              >
                <img
                  src={awayFlag}
                  alt={content.away.name}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              </div>
            )}
          </div>

          {/* Featured players (optional) */}
          {(content.featuredHome || content.featuredAway) && (
            <div
              style={{
                marginTop: isStory ? 36 : 24,
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "center",
                gap: isStory ? 80 : 60,
              }}
            >
              {renderPlayer(content.featuredHome, "home")}
              {renderPlayer(content.featuredAway, "away")}
            </div>
          )}

          {/* Tagline pull-quote (optional) */}
          {content.tagline && <div style={taglineStyle}>"{content.tagline}"</div>}

          {/* Date + venue strip */}
          <div
            style={{
              marginTop: isStory ? 44 : 30,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 6,
            }}
          >
            <div style={statLineStyle}>
              {content.dateLabel}
              {content.kickoffLabel && (
                <>
                  <span style={{ color: accentColor, opacity: 0.7, margin: "0 12px" }}>·</span>
                  {content.kickoffLabel}
                </>
              )}
            </div>
            <div
              style={{
                ...statLineStyle,
                color: isEditorial
                  ? "rgba(26,46,31,0.65)"
                  : isMono
                    ? (monoBgIsLight ? "rgba(0,0,0,0.55)" : "rgba(255,255,255,0.7)")
                    : "rgba(255,255,255,0.7)",
                fontSize: ((statLineStyle.fontSize as number) || 24) * 0.85,
              }}
            >
              {content.venue}
              {content.city && (
                <>
                  <span style={{ color: accentColor, opacity: 0.5, margin: "0 10px" }}>·</span>
                  {content.city}
                </>
              )}
            </div>
          </div>

          {/* CTA (optional) */}
          {content.ctaText && <div style={ctaStyle}>{content.ctaText} →</div>}
        </div>
      </FbkCardShell>
    );
  },
);

export default MatchdayCard;

// ─── Fight-poster layouts (rivalry / duel / marquee) ────────────────────────
// These three themes share a "head-to-head dramatic poster" composition that
// diverges from the standard Matchday layout. Each takes the same content +
// returns a fully bespoke JSX tree wrapped in FbkCardShell so brand header /
// footer keep working.

type FightTheme = "rivalry" | "duel" | "marquee";

interface FightPosterProps {
  format: CardFormat;
  content: MatchdayCardContent;
  theme: FightTheme;
}

const FightPosterLayout = forwardRef<HTMLDivElement, FightPosterProps>(
  function FightPosterLayout({ format, content, theme }, ref) {
    const isStory = format === "story";
    const style = content.style ?? {};
    const homeColors = getNationColors(content.home.code);
    const awayColors = getNationColors(content.away.code);
    const homeColor = content.homeColor || homeColors.primary;
    const awayColor = content.awayColor || awayColors.primary;
    const homeFlag = flagSrc(content.home);
    const awayFlag = flagSrc(content.away);

    // Theme-tuned base gradient. Each theme also gets its own decorative
    // overlay set inside FbkCardShell below.
    const customBaseGradient =
      theme === "rivalry"
        ? // Flag halves render as <img>s; base is just dark fill behind them
          "linear-gradient(180deg, #050505 0%, #0d0d0d 100%)"
        : theme === "duel"
          ? // Pure cinematic black; dual radial spotlights painted in overlay
            "radial-gradient(ellipse 80% 60% at 50% 55%, #14140d 0%, #050505 70%, #000000 100%)"
          : // marquee — diagonal split with home/gold/away
            `linear-gradient(135deg,
              ${homeColor} 0%, ${homeColor} 32%,
              #FFD700 32%, #FFD700 34%,
              #0A0A0A 34%, #0A0A0A 66%,
              #FFD700 66%, #FFD700 68%,
              ${awayColor} 68%, ${awayColor} 100%)`;

    return (
      <FbkCardShell
        ref={ref}
        format={format}
        footerLeft={content.footerLeft || defaultFooterLeft("MATCHDAY")}
        footerRight={content.hashtags || "#WC2026 · #MATCHDAY · #FBK"}
        header={content.header}
        background={style.background}
        customBaseGradient={customBaseGradient}
        suppressBrandOverlays
        hideFooter={content.footer?.hide}
      >
        {theme === "rivalry" && (
          <RivalryBody
            isStory={isStory}
            content={content}
            homeFlag={homeFlag}
            awayFlag={awayFlag}
          />
        )}
        {theme === "duel" && (
          <DuelBody
            isStory={isStory}
            content={content}
            homeColor={homeColor}
            awayColor={awayColor}
            homeFlag={homeFlag}
            awayFlag={awayFlag}
          />
        )}
        {theme === "marquee" && (
          <MarqueeBody
            isStory={isStory}
            content={content}
            homeColor={homeColor}
            awayColor={awayColor}
          />
        )}
      </FbkCardShell>
    );
  },
);

// ─── RIVALRY ── Two flag halves + giant centre headline + narrative tag.
//   The closest match to the "Deja Vu?" reference poster: dramatic, broadcast-y,
//   driven by the admin's `tagline` as the hero text.

function RivalryBody({
  isStory,
  content,
  homeFlag,
  awayFlag,
}: {
  isStory: boolean;
  content: MatchdayCardContent;
  homeFlag: string | undefined;
  awayFlag: string | undefined;
}) {
  const headlineFontSize = isStory ? 240 : 168;
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        zIndex: 2,
      }}
    >
      {/* Flag halves — fill the entire card, darkened so text reads */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
        }}
      >
        <FlagHalf src={homeFlag} side="left" />
        <FlagHalf src={awayFlag} side="right" />
      </div>

      {/* Dark vignette under the centre band so the giant text always pops */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(ellipse 60% 70% at 50% 50%, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.35) 50%, rgba(0,0,0,0.55) 100%)",
          pointerEvents: "none",
        }}
      />

      {/* Eyebrow */}
      <div
        style={{
          position: "relative",
          fontFamily: FONT_INTER,
          fontSize: isStory ? 22 : 18,
          fontWeight: 800,
          color: "#FFD700",
          letterSpacing: "0.5em",
          textTransform: "uppercase",
          marginTop: isStory ? 48 : 28,
          textAlign: "center",
          textShadow: "0 2px 8px rgba(0,0,0,0.6)",
        }}
      >
        {content.eyebrow}
      </div>

      {/* Hero question / dramatic centre headline — pulls from `tagline` */}
      {content.tagline && (
        <div
          style={{
            position: "relative",
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: `0 ${isStory ? 60 : 40}px`,
          }}
        >
          <div
            style={{
              fontFamily: FONT_BEBAS,
              fontSize: headlineFontSize,
              fontWeight: 800,
              color: "#FFFFFF",
              letterSpacing: "-0.02em",
              lineHeight: 0.9,
              textAlign: "center",
              textTransform: "uppercase",
              textShadow:
                "0 8px 32px rgba(0,0,0,0.7), 0 2px 4px rgba(0,0,0,0.9)",
            }}
          >
            {content.tagline}
          </div>
        </div>
      )}

      {/* Featured player portraits — large, side-by-side over their flag halves */}
      <FightPlayerPortraits
        isStory={isStory}
        featuredHome={content.featuredHome}
        featuredAway={content.featuredAway}
        ringColor="rgba(255,255,255,0.4)"
        photoSize={isStory ? 360 : 260}
        nameStyle="overlay"
      />

      {/* Bottom strip: flag pair + VS + date + narrative tag */}
      <RivalryFooterStrip
        isStory={isStory}
        homeFlag={homeFlag}
        awayFlag={awayFlag}
        dateLabel={content.dateLabel}
        venue={content.venue}
        narrativeTag={content.narrativeTag}
      />
    </div>
  );
}

function FlagHalf({
  src,
  side,
}: {
  src: string | undefined;
  side: "left" | "right";
}) {
  if (!src) {
    return (
      <div style={{ flex: 1, background: side === "left" ? "#0a0a0a" : "#1a1a1a" }} />
    );
  }
  return (
    <div
      style={{
        flex: 1,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <img
        src={src}
        alt=""
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          // Slight side fade so the centre gap reads as the dramatic seam
          maskImage:
            side === "left"
              ? "linear-gradient(90deg, #000 0%, #000 70%, transparent 100%)"
              : "linear-gradient(90deg, transparent 0%, #000 30%, #000 100%)",
          WebkitMaskImage:
            side === "left"
              ? "linear-gradient(90deg, #000 0%, #000 70%, transparent 100%)"
              : "linear-gradient(90deg, transparent 0%, #000 30%, #000 100%)",
          filter: "brightness(0.55) saturate(1.15)",
        }}
      />
    </div>
  );
}

function RivalryFooterStrip({
  isStory,
  homeFlag,
  awayFlag,
  dateLabel,
  venue,
  narrativeTag,
}: {
  isStory: boolean;
  homeFlag: string | undefined;
  awayFlag: string | undefined;
  dateLabel: string;
  venue: string;
  narrativeTag?: string;
}) {
  const miniFlag = isStory ? 80 : 60;
  return (
    <div
      style={{
        position: "relative",
        marginBottom: isStory ? 36 : 20,
        display: "flex",
        flexDirection: "column",
        gap: isStory ? 18 : 12,
        alignItems: "center",
      }}
    >
      {/* Mini flag + VS + mini flag strip */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: isStory ? 28 : 20,
        }}
      >
        {homeFlag && (
          <img
            src={homeFlag}
            alt=""
            style={{
              width: miniFlag,
              height: miniFlag * 0.65,
              objectFit: "cover",
              borderRadius: 6,
              border: "2px solid rgba(255,255,255,0.6)",
              boxShadow: "0 4px 12px rgba(0,0,0,0.6)",
            }}
          />
        )}
        <div
          style={{
            fontFamily: FONT_BEBAS,
            fontSize: isStory ? 56 : 42,
            color: "#FFD700",
            letterSpacing: "0.1em",
            textShadow: "0 4px 16px rgba(255,215,0,0.5)",
          }}
        >
          VS
        </div>
        {awayFlag && (
          <img
            src={awayFlag}
            alt=""
            style={{
              width: miniFlag,
              height: miniFlag * 0.65,
              objectFit: "cover",
              borderRadius: 6,
              border: "2px solid rgba(255,255,255,0.6)",
              boxShadow: "0 4px 12px rgba(0,0,0,0.6)",
            }}
          />
        )}
      </div>

      {/* Narrative tag — historical context pill */}
      {narrativeTag && (
        <div
          style={{
            background: "rgba(0,0,0,0.7)",
            border: "1px solid rgba(255,215,0,0.4)",
            padding: `${isStory ? 10 : 8}px ${isStory ? 24 : 18}px`,
            borderRadius: 8,
            fontFamily: FONT_BEBAS,
            fontSize: isStory ? 22 : 17,
            color: "#FFD700",
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            textShadow: "0 2px 6px rgba(0,0,0,0.8)",
          }}
        >
          {narrativeTag}
        </div>
      )}

      {/* Date + venue */}
      <div
        style={{
          fontFamily: "ui-monospace, 'JetBrains Mono', monospace",
          fontSize: isStory ? 18 : 14,
          fontWeight: 700,
          color: "rgba(255,255,255,0.85)",
          letterSpacing: "0.25em",
          textTransform: "uppercase",
          textAlign: "center",
        }}
      >
        {dateLabel} · {venue}
      </div>
    </div>
  );
}

// ─── DUEL ── Cinematic black + dual nation-coloured spotlights + circular portraits.
//   More elegant / minimal than rivalry. Lets the players carry the drama.

function DuelBody({
  isStory,
  content,
  homeColor,
  awayColor,
  homeFlag,
  awayFlag,
}: {
  isStory: boolean;
  content: MatchdayCardContent;
  homeColor: string;
  awayColor: string;
  homeFlag: string | undefined;
  awayFlag: string | undefined;
}) {
  // Photo block height — must eat most of the card so the players
  // visually fill the frame, not just the top half. Source photos usually
  // include some bottom dark padding (head-to-knee portraits on a black
  // backdrop) — if the block is too short, that padding reads as empty
  // black dead space below the players. 78% (story) / 70% (post) keeps
  // the photos dominant while leaving a compact info band underneath.
  const photoBlockHeight = isStory ? "78%" : "70%";

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        zIndex: 2,
      }}
    >
      {/* Twin tint cones using nation colours — sit BEHIND the photos */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: [
            `radial-gradient(ellipse 60% 80% at 18% 50%, ${homeColor}33 0%, transparent 60%)`,
            `radial-gradient(ellipse 60% 80% at 82% 50%, ${awayColor}33 0%, transparent 60%)`,
          ].join(", "),
          pointerEvents: "none",
        }}
      />

      {/* Eyebrow — overlays the top of the photo block */}
      <div
        style={{
          position: "relative",
          fontFamily: FONT_INTER,
          fontSize: isStory ? 22 : 18,
          fontWeight: 800,
          color: "#FFD700",
          letterSpacing: "0.5em",
          textTransform: "uppercase",
          marginTop: isStory ? 36 : 22,
          textShadow: "0 2px 8px rgba(0,0,0,0.7)",
          textAlign: "center",
          zIndex: 4,
        }}
      >
        {content.eyebrow}
      </div>

      {/* ── Full-bleed photo split ──────────────────────────────────────
         Two columns, each photo fills its half edge-to-edge (objectFit:
         cover, objectPosition: top center so the player's head stays in
         frame across portrait crops). No border, no circle — just the
         player against the nation-tinted backdrop. Subtle bottom-to-top
         shadow makes the seam + VS read against the photos. */}
      <div
        style={{
          position: "relative",
          width: "100%",
          height: photoBlockHeight,
          marginTop: isStory ? 16 : 10,
          display: "flex",
        }}
      >
        <PhotoHalf
          player={content.featuredHome}
          side="left"
          tint={homeColor}
          isStory={isStory}
        />
        <PhotoHalf
          player={content.featuredAway}
          side="right"
          tint={awayColor}
          isStory={isStory}
        />

        {/* Vertical gold seam down the centre */}
        <div
          style={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: "50%",
            width: 2,
            background:
              "linear-gradient(180deg, transparent 0%, rgba(255,215,0,0.55) 18%, rgba(255,215,0,0.85) 50%, rgba(255,215,0,0.55) 82%, transparent 100%)",
            boxShadow: "0 0 24px rgba(255,215,0,0.45)",
            zIndex: 3,
          }}
        />

        {/* Giant central VS — overlays the seam */}
        <div
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            fontFamily: FONT_BEBAS,
            fontSize: isStory ? 280 : 200,
            fontWeight: 800,
            color: "#FFD700",
            letterSpacing: "0.02em",
            lineHeight: 1,
            zIndex: 4,
            textShadow:
              "0 0 80px rgba(255,215,0,0.5), 0 8px 32px rgba(0,0,0,0.85), 0 2px 4px rgba(0,0,0,0.95)",
          }}
        >
          VS
        </div>

        {/* Player name strips — sit at the bottom of each half over the
            shadow gradient. Bebas + uppercase, in nation tint. */}
        <PlayerNameStrip
          player={content.featuredHome}
          side="left"
          isStory={isStory}
        />
        <PlayerNameStrip
          player={content.featuredAway}
          side="right"
          isStory={isStory}
        />
      </div>

      {/* ── Bottom info band ───────────────────────────────────────────
         Flags + ROUND chip + matchup line + date. Packs to the TOP of the
         remaining space (justifyContent: flex-start, not center) so it
         sits right under the photos with no dead black gap between. */}
      <div
        style={{
          position: "relative",
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          padding: `${isStory ? 18 : 12}px ${isStory ? 40 : 28}px ${isStory ? 8 : 6}px`,
          gap: isStory ? 12 : 8,
        }}
      >
        {/* Big flag pair */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: isStory ? 36 : 24,
          }}
        >
          {homeFlag && <BigFlag src={homeFlag} isStory={isStory} />}
          {content.narrativeTag && (
            <div
              style={{
                fontFamily: FONT_BEBAS,
                fontSize: isStory ? 84 : 60,
                color: "#FFD700",
                letterSpacing: "0.02em",
                lineHeight: 1,
                textShadow:
                  "0 4px 16px rgba(0,0,0,0.7), 0 2px 4px rgba(0,0,0,0.9)",
              }}
            >
              {content.narrativeTag}
            </div>
          )}
          {awayFlag && <BigFlag src={awayFlag} isStory={isStory} />}
        </div>

        {/* Matchup names */}
        <div
          style={{
            fontFamily: FONT_BEBAS,
            fontSize: isStory ? 46 : 32,
            color: "#FFFFFF",
            letterSpacing: "0.06em",
            textShadow: "0 2px 12px rgba(0,0,0,0.85)",
            textAlign: "center",
          }}
        >
          {content.home.name.toUpperCase()} VS {content.away.name.toUpperCase()}
        </div>

        {/* Date + venue */}
        <div
          style={{
            fontFamily: "ui-monospace, 'JetBrains Mono', monospace",
            fontSize: isStory ? 20 : 15,
            fontWeight: 700,
            color: "rgba(255,255,255,0.85)",
            letterSpacing: "0.25em",
            textTransform: "uppercase",
            textAlign: "center",
          }}
        >
          {content.dateLabel} · {content.venue}
        </div>

        {/* Optional tagline */}
        {content.tagline && (
          <div
            style={{
              fontFamily: FONT_INTER,
              fontSize: isStory ? 22 : 17,
              fontStyle: "italic",
              color: "rgba(255,215,0,0.92)",
              textAlign: "center",
              maxWidth: isStory ? 780 : 640,
              lineHeight: 1.3,
              marginTop: isStory ? 4 : 2,
            }}
          >
            "{content.tagline}"
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Full-bleed half-photo for the Duel layout. Each takes 50% width, 100%
 * height of the photo block, with `objectFit: cover` + `objectPosition: top`
 * so the player's head sits in frame. A bottom-up dark gradient overlays the
 * photo so the player name strip and centre VS stay legible.
 */
function PhotoHalf({
  player,
  side,
  tint,
  isStory,
}: {
  player: MatchdayCardContent["featuredHome"];
  side: "left" | "right";
  tint: string;
  isStory: boolean;
}) {
  return (
    <div
      style={{
        flex: 1,
        position: "relative",
        overflow: "hidden",
        background: `linear-gradient(180deg, ${tint}33 0%, ${tint}11 60%, #0a0a0a 100%)`,
      }}
    >
      {player?.photoUrl ? (
        <img
          src={player.photoUrl}
          alt={player.name}
          crossOrigin={
            player.photoUrl.startsWith("data:") ? undefined : "anonymous"
          }
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "top center",
            display: "block",
          }}
        />
      ) : (
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: FONT_BEBAS,
            fontSize: isStory ? 180 : 130,
            color: "rgba(255,255,255,0.18)",
            letterSpacing: "0.04em",
          }}
        >
          {player?.name?.[0]?.toUpperCase() ?? "?"}
        </div>
      )}
      {/* Side fade toward the centre seam — lifts the VS off the photos */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          background:
            side === "left"
              ? "linear-gradient(90deg, transparent 60%, rgba(0,0,0,0.65) 100%)"
              : "linear-gradient(270deg, transparent 60%, rgba(0,0,0,0.65) 100%)",
          pointerEvents: "none",
        }}
      />
      {/* Bottom-to-top dark gradient — readability for the name strip */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(180deg, transparent 50%, rgba(0,0,0,0.75) 90%, rgba(0,0,0,0.9) 100%)",
          pointerEvents: "none",
        }}
      />
    </div>
  );
}

/** Player name + jersey number rendered along the bottom of their half. */
function PlayerNameStrip({
  player,
  side,
  isStory,
}: {
  player: MatchdayCardContent["featuredHome"];
  side: "left" | "right";
  isStory: boolean;
}) {
  if (!player) return null;
  return (
    <div
      style={{
        position: "absolute",
        bottom: isStory ? 22 : 14,
        ...(side === "left"
          ? { left: isStory ? 32 : 22 }
          : { right: isStory ? 32 : 22 }),
        zIndex: 3,
        textAlign: side === "left" ? "left" : "right",
        maxWidth: "42%",
      }}
    >
      <div
        style={{
          fontFamily: FONT_BEBAS,
          fontSize: isStory ? 42 : 30,
          color: "#FFFFFF",
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          lineHeight: 1,
          textShadow:
            "0 4px 14px rgba(0,0,0,0.85), 0 2px 4px rgba(0,0,0,0.95)",
        }}
      >
        {player.name}
      </div>
    </div>
  );
}

/** Larger flag tile for the lower-third matchup strip. */
function BigFlag({ src, isStory }: { src: string; isStory: boolean }) {
  const w = isStory ? 130 : 92;
  return (
    <img
      src={src}
      alt=""
      crossOrigin={src.startsWith("data:") ? undefined : "anonymous"}
      style={{
        width: w,
        height: w * 0.7,
        objectFit: "cover",
        borderRadius: 8,
        border: "2px solid rgba(255,255,255,0.85)",
        boxShadow: "0 8px 22px rgba(0,0,0,0.65)",
        flexShrink: 0,
      }}
    />
  );
}

// ─── MARQUEE ── Fight-night / boxing-poster aesthetic. Aggressive diagonals,
//   bold slab typography. Player portraits in rectangular cutouts.

function MarqueeBody({
  isStory,
  content,
  homeColor,
  awayColor,
}: {
  isStory: boolean;
  content: MatchdayCardContent;
  homeColor: string;
  awayColor: string;
}) {
  // We don't use homeColor/awayColor as text here (gradient already shows them);
  // keep refs to silence the unused-arg warning while leaving room for future tuning.
  void homeColor;
  void awayColor;
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        zIndex: 2,
      }}
    >
      {/* Top marquee strip */}
      <div
        style={{
          marginTop: isStory ? 60 : 36,
          padding: `${isStory ? 14 : 10}px ${isStory ? 36 : 24}px`,
          background: "#0A0A0A",
          alignSelf: "center",
          border: "3px solid #FFD700",
          fontFamily: FONT_BEBAS,
          fontSize: isStory ? 32 : 24,
          color: "#FFD700",
          letterSpacing: "0.35em",
          textTransform: "uppercase",
        }}
      >
        {content.eyebrow || "MAIN EVENT"}
      </div>

      {/* Player portraits in rectangular cutouts (or initials fallback) */}
      <div
        style={{
          marginTop: isStory ? 48 : 28,
          flex: 1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: `0 ${isStory ? 60 : 36}px`,
          gap: isStory ? 40 : 24,
        }}
      >
        <MarqueeCutout
          player={content.featuredHome}
          codeLabel={content.home.code.toUpperCase()}
          accentColor="#FFFFFF"
          isStory={isStory}
        />

        {/* Centre stack: nation names stacked + VS */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: isStory ? 4 : 2,
            flexShrink: 0,
          }}
        >
          <div
            style={{
              fontFamily: FONT_BEBAS,
              fontSize: isStory ? 96 : 68,
              color: "#FFFFFF",
              letterSpacing: "-0.01em",
              lineHeight: 0.95,
              textTransform: "uppercase",
              textShadow: "0 4px 12px rgba(0,0,0,0.5)",
            }}
          >
            {content.home.name}
          </div>
          <div
            style={{
              fontFamily: FONT_BEBAS,
              fontSize: isStory ? 80 : 56,
              color: "#FFD700",
              letterSpacing: "0.15em",
              lineHeight: 1,
              padding: `${isStory ? 6 : 4}px 0`,
            }}
          >
            VS
          </div>
          <div
            style={{
              fontFamily: FONT_BEBAS,
              fontSize: isStory ? 96 : 68,
              color: "#FFFFFF",
              letterSpacing: "-0.01em",
              lineHeight: 0.95,
              textTransform: "uppercase",
              textShadow: "0 4px 12px rgba(0,0,0,0.5)",
            }}
          >
            {content.away.name}
          </div>
        </div>

        <MarqueeCutout
          player={content.featuredAway}
          codeLabel={content.away.code.toUpperCase()}
          accentColor="#FFFFFF"
          isStory={isStory}
        />
      </div>

      {/* Tagline */}
      {content.tagline && (
        <div
          style={{
            alignSelf: "center",
            fontFamily: FONT_INTER,
            fontSize: isStory ? 26 : 20,
            fontStyle: "italic",
            color: "#FFFFFF",
            textAlign: "center",
            maxWidth: isStory ? 780 : 620,
            padding: `0 ${isStory ? 40 : 28}px`,
            lineHeight: 1.3,
            textShadow: "0 2px 8px rgba(0,0,0,0.7)",
          }}
        >
          "{content.tagline}"
        </div>
      )}

      {/* Bottom marquee bar: date + venue + narrative tag */}
      <div
        style={{
          marginTop: isStory ? 24 : 16,
          marginBottom: isStory ? 36 : 20,
          alignSelf: "center",
          background: "#0A0A0A",
          border: "2px solid #FFD700",
          padding: `${isStory ? 12 : 8}px ${isStory ? 32 : 20}px`,
          display: "flex",
          alignItems: "center",
          gap: isStory ? 22 : 14,
        }}
      >
        <span
          style={{
            fontFamily: FONT_BEBAS,
            fontSize: isStory ? 28 : 20,
            color: "#FFD700",
            letterSpacing: "0.3em",
            textTransform: "uppercase",
          }}
        >
          {content.dateLabel}
        </span>
        <span style={{ color: "#FFD700", opacity: 0.5 }}>·</span>
        <span
          style={{
            fontFamily: FONT_BEBAS,
            fontSize: isStory ? 24 : 18,
            color: "#FFFFFF",
            letterSpacing: "0.25em",
            textTransform: "uppercase",
          }}
        >
          {content.venue}
        </span>
        {content.narrativeTag && (
          <>
            <span style={{ color: "#FFD700", opacity: 0.5 }}>·</span>
            <span
              style={{
                fontFamily: FONT_BEBAS,
                fontSize: isStory ? 24 : 18,
                color: "#FFD700",
                letterSpacing: "0.25em",
                textTransform: "uppercase",
              }}
            >
              {content.narrativeTag}
            </span>
          </>
        )}
      </div>
    </div>
  );
}

function MarqueeCutout({
  player,
  codeLabel,
  accentColor,
  isStory,
}: {
  player: MatchdayCardContent["featuredHome"];
  codeLabel: string;
  accentColor: string;
  isStory: boolean;
}) {
  const w = isStory ? 240 : 170;
  const h = isStory ? 320 : 230;
  if (!player) {
    return (
      <div
        style={{
          width: w,
          height: h,
          background: "rgba(0,0,0,0.4)",
          border: "3px solid rgba(255,255,255,0.2)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: FONT_BEBAS,
          fontSize: isStory ? 64 : 48,
          color: accentColor,
          letterSpacing: "0.1em",
          flexShrink: 0,
        }}
      >
        {codeLabel}
      </div>
    );
  }
  const initials = player.name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("");
  return (
    <div
      style={{
        position: "relative",
        width: w,
        height: h,
        background: "#0A0A0A",
        border: "3px solid #FFD700",
        overflow: "hidden",
        boxShadow: "0 14px 36px rgba(0,0,0,0.65)",
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {player.photoUrl ? (
        <img
          src={player.photoUrl}
          alt={player.name}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : (
        <div
          style={{
            fontFamily: FONT_BEBAS,
            fontSize: w * 0.4,
            color: "#FFFFFF",
          }}
        >
          {initials}
        </div>
      )}
      {/* Bottom name strip */}
      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          padding: `${isStory ? 8 : 6}px ${isStory ? 12 : 8}px`,
          background: "linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.95) 100%)",
          fontFamily: FONT_BEBAS,
          fontSize: isStory ? 26 : 18,
          color: "#FFFFFF",
          letterSpacing: "0.05em",
          textTransform: "uppercase",
          textAlign: "center",
        }}
      >
        {player.name}
      </div>
      {/* Top-left code tag */}
      <div
        style={{
          position: "absolute",
          top: isStory ? 10 : 6,
          left: isStory ? 10 : 6,
          background: "#FFD700",
          color: "#0A0A0A",
          padding: `${isStory ? 4 : 3}px ${isStory ? 10 : 7}px`,
          fontFamily: FONT_BEBAS,
          fontSize: isStory ? 20 : 15,
          letterSpacing: "0.15em",
        }}
      >
        {codeLabel}
      </div>
    </div>
  );
}

// Shared portrait-pair component used by rivalry layout. Different from
// CircularPortrait/MarqueeCutout — these are rectangular silhouettes meant
// to sit on top of flag halves.
function FightPlayerPortraits({
  isStory,
  featuredHome,
  featuredAway,
  ringColor,
  photoSize,
  nameStyle,
}: {
  isStory: boolean;
  featuredHome: MatchdayCardContent["featuredHome"];
  featuredAway: MatchdayCardContent["featuredAway"];
  ringColor: string;
  photoSize: number;
  nameStyle: "overlay" | "below";
}) {
  if (!featuredHome && !featuredAway) return null;
  void nameStyle;
  void ringColor;
  const renderOne = (
    p: MatchdayCardContent["featuredHome"],
    side: "left" | "right",
  ) => {
    if (!p) return <div style={{ width: photoSize, height: photoSize }} />;
    const initials = p.name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((s) => s[0]?.toUpperCase() ?? "")
      .join("");
    return (
      <div
        style={{
          position: "relative",
          width: photoSize,
          height: photoSize,
          flexShrink: 0,
        }}
      >
        <div
          style={{
            width: photoSize,
            height: photoSize,
            overflow: "hidden",
            background:
              "linear-gradient(180deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.9) 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 14px 36px rgba(0,0,0,0.7)",
          }}
        >
          {p.photoUrl ? (
            <img
              src={p.photoUrl}
              alt={p.name}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: side === "left" ? "right" : "left",
              }}
            />
          ) : (
            <div
              style={{
                fontFamily: FONT_BEBAS,
                fontSize: photoSize * 0.4,
                color: "#FFFFFF",
              }}
            >
              {initials}
            </div>
          )}
        </div>
      </div>
    );
  };
  return (
    <div
      style={{
        position: "absolute",
        bottom: isStory ? 280 : 200,
        left: 0,
        right: 0,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-end",
        padding: `0 ${isStory ? 30 : 16}px`,
        pointerEvents: "none",
      }}
    >
      {renderOne(featuredHome, "left")}
      {renderOne(featuredAway, "right")}
    </div>
  );
}
